/**
 * Decoration Engine — placement rules for ornaments.
 *
 * Decorations are the only objects allowed to break the safe area, but they
 * must never sit on top of text zones. This engine owns the rules of where
 * ornaments may appear: named slots, per-style density caps, and slot
 * geometry. It returns placements (slot + rect + component props); the
 * composition engine turns them into component builds.
 */

import { rectOverlapArea } from './layoutEngine';

/** Slot geometry as fractions of the canvas. Size is per-decoration-style. */
const SLOT_ANCHORS = {
  'top-left': { x: 0, y: 0 },
  'top-right': { x: 1, y: 0 },
  'bottom-left': { x: 0, y: 1 },
  'bottom-right': { x: 1, y: 1 },
  'top-edge': { x: 0.5, y: 0 },
  'bottom-edge': { x: 0.5, y: 1 },
  'right-edge': { x: 1, y: 0.5 },
  'behind-hero': { x: 0.5, y: 0.2 },
};

export const DECORATION_STYLES = {
  none: { id: 'none', name: 'None', maxDecorations: 0, sizeFrac: 0, components: [] },
  minimal: {
    id: 'minimal', name: 'Minimal', maxDecorations: 2, sizeFrac: 0.18,
    components: [{ id: 'decorativeShape', props: { shape: 'circle', opacity: 0.18 } }],
  },
  organic: {
    id: 'organic', name: 'Organic', maxDecorations: 3, sizeFrac: 0.3,
    components: [
      { id: 'blob', props: { opacity: 0.3 } },
      { id: 'wave', props: {} },
    ],
  },
  geometric: {
    id: 'geometric', name: 'Geometric', maxDecorations: 4, sizeFrac: 0.22,
    components: [
      { id: 'decorativeShape', props: { shape: 'triangle', opacity: 0.25, rotation: 15 } },
      { id: 'decorativeShape', props: { shape: 'circle', opacity: 0.2 } },
    ],
  },
};

export const listDecorationStyles = () => Object.keys(DECORATION_STYLES);

const slotRect = (slot, canvas, size) => {
  const anchor = SLOT_ANCHORS[slot];
  if (!anchor) throw new Error(`Unknown decoration slot "${slot}"`);
  // Anchor the box so it hugs its corner/edge, half-bleeding off-canvas is fine
  // for decorations — the editor clips them.
  const cx = anchor.x * canvas.width;
  const cy = anchor.y * canvas.height;
  return {
    x: Math.round(cx - size / 2),
    y: Math.round(cy - size / 2),
    width: Math.round(size),
    height: Math.round(size),
  };
};

/**
 * Plan decoration placements for a resolved layout.
 * Returns [{ slot, componentId, props, rect }] obeying:
 *  - only slots the layout preset allows
 *  - the style's density cap
 *  - keep-out: a decoration may not cover more than 15% of any text zone
 *    (behind-hero is exempt — it is explicitly a backdrop slot).
 */
export const planDecorations = (style, layout) => {
  const styleDef = DECORATION_STYLES[style];
  if (!styleDef) throw new Error(`Unknown decoration style "${style}"`);
  if (!styleDef.maxDecorations) return [];

  const size = styleDef.sizeFrac * Math.min(layout.canvas.width, layout.canvas.height);
  const textZones = Object.values(layout.zones).filter((z) => z.kind === 'text');
  const placements = [];

  for (const slot of layout.decorationSlots) {
    if (placements.length >= styleDef.maxDecorations) break;
    const component = styleDef.components[placements.length % styleDef.components.length];
    const rect = slotRect(slot, layout.canvas, size);

    if (slot !== 'behind-hero') {
      const covers = textZones.some((zone) => {
        const zoneArea = zone.width * zone.height;
        return zoneArea > 0 && rectOverlapArea(rect, zone) / zoneArea > 0.15;
      });
      if (covers) continue;
    }

    placements.push({ slot, componentId: component.id, props: { ...component.props }, rect });
  }
  return placements;
};
